import { createGenerateClassName, createTheme, CssBaseline } from '@material-ui/core';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import { StylesProvider, ThemeProvider as MaterialThemeProvider } from '@material-ui/styles';
import { useMemo } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { overrideAppBar } from "../app-bar/AppBarOverrides.js";
import { overrideAutocomplete } from "../autocomplete/AutocompleteOverrides.js";
import { overrideAvatar } from "../avatar/AvatarOverrides.js";
import { overrideButton } from "../button/ButtonOverrides.js";
import { overrideCard } from "../card/CardOverrides.js";
import { overrideCheckbox } from "../checkbox/CheckboxOverrides.js";
import { overrideChip } from "../chip/ChipOverrides.js";
import { overrideDialog } from "../dialog/DialogOverrides.js";
import { overrideDrawer } from "../drawer/DrawerOverrides.js";
import { overrideIconButton } from "../icon-button/IconButtonOverrides.js";
import { overrideLink } from "../link/LinkOverrides.js";
import { overrideList } from "../list/ListOverrides.js";
import { overrideMenu } from "../menu/MenuOverrides.js";
import { overridePagination } from "../pagination/PaginationOverrides.js";
import { overridePaper } from "../paper/PaperOverrides.js";
import { overridePreloader } from "../preloader/PreLoaderOverrides.js";
import { overrideRadio } from "../radio/RadioOverrides.js";
import { ResponsiveContextProvider } from "../responsive/ResponsiveContext.js";
import { overrideSnackbar } from "../snackbar/SnackbarOverrides.js";
import { SnackbarStackProvider } from "../snackbar/SnackbarStack.js";
import { overrideSvgIcon } from "../svg-icon/SvgIconOverrides.js";
import { overrideSwitch } from "../switch/SwitchOverrides.js";
import { overrideTabs } from "../tabs/TabsOverrides.js";
import { overrideTextField } from "../text-field/TextFieldOverrides.js";
import { overrideToolbar } from "../toolbar/ToolbarOverrides.js";
import { overrideTooltip } from "../tooltip/TooltipOverrides.js";
import { createTypographyOptions, overrideTypography } from "../typography/TypographyOverrides.js";
import { Color, ColorDark } from "./Color.js";
import { GlobalStyles } from "./GlobalStyles.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function createSuperDispatchTheme(type) {
  var breakpoints = createBreakpoints({});
  var color = type === 'dark' ? ColorDark : Color;
  var theme = createTheme({
    breakpoints,
    palette: {
      type,
      primary: {
        main: color.Blue300
      },
      error: {
        main: color.Red300
      },
      action: {
        hover: color.Silver200,
        selected: color.Blue50,
        disabled: color.Silver400
      },
      text: {
        primary: color.Dark500,
        secondary: color.Dark300,
        hint: color.Dark100,
        disabled: color.Dark100
      },
      common: {
        white: color.White,
        black: color.Black
      },
      divider: color.Silver400,
      background: {
        paper: color.White
      }
    },
    typography: createTypographyOptions(breakpoints),
    props: {},
    overrides: {}
  });
  overrideAppBar(theme);
  overrideAutocomplete(theme);
  overrideAvatar(theme);
  overrideButton(theme);
  overrideCard(theme);
  overrideCheckbox(theme);
  overrideChip(theme);
  overrideDialog(theme);
  overrideDrawer(theme);
  overrideIconButton(theme);
  overrideLink(theme);
  overrideList(theme);
  overrideMenu(theme);
  overridePaper(theme);
  overrideRadio(theme);
  overrideSnackbar(theme);
  overrideSvgIcon(theme);
  overrideSwitch(theme);
  overrideTabs(theme);
  overrideTextField(theme);
  overrideToolbar(theme);
  overrideTooltip(theme);
  overrideTypography(theme);
  overridePagination(theme);
  overridePreloader(theme);
  return theme;
}
var generateMaterialClassName = /*#__PURE__*/createGenerateClassName();
function generateClassName(rule, sheet) {
  var {
    meta,
    link
  } = (sheet === null || sheet === void 0 ? void 0 : sheet.options) || {};
  if (meta && rule.type === 'style') {
    if (meta.startsWith('MuiSnackbar') && sheet) {
      return "SDSnackbar-".concat(rule.key);
    }
    if (meta.startsWith('SD-')) {
      return "".concat(meta, "-").concat(rule.key);
    }
    if (meta.startsWith('Mui')) {
      var isPseudoClass = ['checked', 'disabled', 'error', 'focused', 'focusVisible', 'required', 'expanded', 'selected'].includes(rule.key);
      if (isPseudoClass) {
        return "Mui-".concat(rule.key);
      }
      if (!link) {
        return "".concat(meta, "-").concat(rule.key);
      }
    }
  }
  return generateMaterialClassName(rule, sheet);
}
export function ThemeProvider(_ref) {
  var {
    modifier,
    children,
    mode,
    injectFirst = true
  } = _ref;
  var theme = useMemo(() => {
    var nextTheme = createSuperDispatchTheme(mode);
    return !modifier ? nextTheme : modifier(nextTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- modifier may update on every render
  }, [mode]);
  return /*#__PURE__*/_jsx(StylesProvider, {
    injectFirst: injectFirst,
    generateClassName: generateClassName,
    children: /*#__PURE__*/_jsxs(MaterialThemeProvider, {
      theme: theme,
      children: [/*#__PURE__*/_jsx(CssBaseline, {}), /*#__PURE__*/_jsxs(StyledThemeProvider, {
        theme: theme,
        children: [/*#__PURE__*/_jsx(GlobalStyles, {}), /*#__PURE__*/_jsx(ResponsiveContextProvider, {
          children: /*#__PURE__*/_jsx(SnackbarStackProvider, {
            children: children
          })
        })]
      })]
    })
  });
}